import SearchIcon from '@mui/icons-material/Search'
import { Autocomplete, InputAdornment, TextField } from '@mui/material'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useSearchResultsCustomers,
  useSearchResultsVehicles
} from '../../hooks'
import { AppRoutes } from '../../models'
import { CustomerEntry, VehicleEntry } from '../../sdk'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import { CustomerListItem } from './CustomerListItem'
import { ListItemLoading } from './ListItemLoading'
import { NoOptionsText } from './NoOptionsText'
import { VehicleListItem } from './VehicleListItem'

interface Props {}

export const SearchWithAutocomplete: FC<Props> = () => {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(true)
  const navigate = useNavigate()

  const {
    matchingCustomers,
    loading: loadingCustomers,
    errorMessage: errorMessageCustomers
  } = useSearchResultsCustomers(inputValue)

  const {
    matchingVehicles,
    loading: loadingVehicles,
    errorMessage: errorMessageVehicles
  } = useSearchResultsVehicles(inputValue)

  const handleClick = useCallback(
    (option: VehicleEntry | CustomerEntry) => {
      if ('info' in option) {
        navigate(AppRoutes.VEHICLES + '/' + option.id!)
        setInputValue('')
        setIsOpen(false)
      } else {
        navigate(AppRoutes.CUSTOMERS + '/' + option.id!)
        setInputValue('')
        setIsOpen(false)
      }
    },
    [navigate]
  )

  useEffect(() => {
    setIsOpen(inputValue !== '')
  }, [isOpen, inputValue])

  return (
    <Autocomplete
      id="customerSearch"
      open={isOpen}
      loading={loadingCustomers || loadingVehicles}
      inputValue={inputValue}
      forcePopupIcon={false}
      filterOptions={(options) => options} // prevent Autocomplete from filtering options that do not match the input value
      onInputChange={(_e: unknown, newValue) => setInputValue(newValue || '')}
      renderOption={(_props, option: VehicleEntry | CustomerEntry) => {
        if ('info' in option) {
          return (
            <VehicleListItem
              key={option.id}
              option={option}
              handleClick={handleClick.bind(null, option)}
            />
          )
        } else {
          return (
            <CustomerListItem
              key={option.id}
              option={option}
              handleClick={handleClick.bind(null, option)}
            />
          )
        }
      }}
      options={[...matchingCustomers, ...matchingVehicles]}
      getOptionLabel={(option) => {
        if ('info' in option) {
          return [
            option.info?.year,
            option.info?.make,
            option.info?.model
          ].join(' ')
        }
        if ('information' in option) {
          return formatName(option.information)
        }
        return 'N/A'
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          fullWidth
          error={!!errorMessageCustomers || !!errorMessageVehicles}
          placeholder="Search customers and vehicles"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main' }} />
              </InputAdornment>
            )
          }}
        />
      )}
      sx={{
        maxWidth: '400px',
        width: '100%',
        backgroundColor: 'white'
      }}
      noOptionsText={
        <NoOptionsText
          errorMessage={errorMessageCustomers || errorMessageVehicles}
        />
      }
      loadingText={<ListItemLoading />}
    />
  )
}
